import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const LOCAL_EMAIL_CHARACTER = /^[A-Za-z0-9!#$%&'*+\-/=?^_`{|}~.]$/;
const DOMAIN_LABEL_CHARACTER = /^[A-Za-z0-9-]$/;
const ALPHANUMERIC = /^[A-Za-z0-9]$/;

export interface InvoiceRecipientsPayload {
  emails: string[];
}

export function canManageInvoiceRecipients(role: string | null | undefined): boolean {
  return role === 'mainaccount' || role === 'deputy';
}

export function splitInvoiceRecipientEmails(value: unknown): string[] {
  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(',')
    .map(email => email.trim())
    .filter(email => email.length > 0);
}

export function normalizeInvoiceRecipientEmails(value: unknown): string {
  return splitInvoiceRecipientEmails(value).join(', ');
}

export function formatInvoiceRecipientEmails(emails: string[] | null | undefined): string {
  return (emails ?? []).join(', ');
}

export function isValidInvoiceRecipientEmail(email: string): boolean {
  if (email.length < 3 || email.length > 254 || /\s/.test(email)) {
    return false;
  }
  const at = email.indexOf('@');
  if (at < 1 || at >= email.length - 1 || at !== email.lastIndexOf('@') || at > 64) {
    return false;
  }
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
    return false;
  }
  if (![...local].every(character => LOCAL_EMAIL_CHARACTER.test(character))) {
    return false;
  }
  if (domain.length > 253 || !domain.includes('.')) {
    return false;
  }
  return domain.split('.').every(label => {
    return (
      label.length >= 1 &&
      label.length <= 63 &&
      ALPHANUMERIC.test(label[0]) &&
      ALPHANUMERIC.test(label[label.length - 1]) &&
      [...label].every(character => DOMAIN_LABEL_CHARACTER.test(character))
    );
  });
}

export function invoiceRecipientEmailsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const emails = splitInvoiceRecipientEmails(control.value);
    const invalid = emails.filter(email => !isValidInvoiceRecipientEmail(email));
    const counts = new Map<string, number>();

    emails.forEach(email => {
      const normalized = email.toLowerCase();
      counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
    });

    const duplicates = Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([email]) => email);
    const errors: ValidationErrors = {};

    if (invalid.length > 0) {
      errors.invalidInvoiceRecipientEmails = { emails: invalid.join(', ') };
    }
    if (duplicates.length > 0) {
      errors.duplicateInvoiceRecipientEmails = { emails: duplicates.join(', ') };
    }
    return Object.keys(errors).length > 0 ? errors : null;
  };
}

export function registerInvoiceRecipientValidationMessages(validationService: {
  addFormatter(type: string, formatter: (params: { emails: string }) => string[]): void;
}): void {
  validationService.addFormatter('invalidInvoiceRecipientEmails', params => [`Invalid email addresses: ${params.emails}.`]);
  validationService.addFormatter('duplicateInvoiceRecipientEmails', params => [`Duplicate email addresses: ${params.emails}.`]);
}
